import { Channel, type DomainId } from '#graphql-operations'
import { notNullish } from '@vueuse/core'
import type { AvailableChannel } from './useInitData/types'

type SetChannelValue =
  | Channel
  | null
  | Ref<undefined | null | Channel | { channel?: Channel | null | undefined }>

type UseSiteContext = {
  /**
   * The current domain.
   */
  activeDomain: DomainId

  /**
   * The current domain hostname.
   */
  activeHostname: string

  /**
   * Whether we are on a production environment.
   */
  isProduction: boolean

  /**
   * The currently active channel.
   */
  activeChannel: Readonly<Ref<Channel>>

  /**
   * The available channels for this domain.
   *
   * This is determined by Drupal by which menus exist. In order to "support"
   * multiple channels for a domain, at least two menus must exist.
   */
  availableChannels: ComputedRef<AvailableChannel[]>

  /**
   * The available languages.
   */
  availableLanguages: string[]

  /**
   * Set the currently active channel.
   */
  setChannel: (value: Channel) => void

  /**
   * Determine whether the given route path (relative) is a channel landing page.
   */
  isChannelLandingPage: (path: string) => boolean
}

function extractChannel(value?: SetChannelValue): Channel {
  if (typeof value === 'string') {
    return value
  } else if (value && typeof value === 'object' && value.value) {
    if (typeof value.value === 'string') {
      return value.value
    } else if (typeof value.value === 'object' && value.value.channel) {
      return value.value.channel
    }
  }

  return Channel.leisure
}

export function useSiteContext(): UseSiteContext {
  const initData = useInitData()
  const channel = useState<Channel>('activeChannel', () => Channel.leisure)
  const activeChannel = readonly(channel)
  // @TODO: Determine environment.
  const isProduction = false

  // Must be reactive because the language might change.
  const availableChannels = computed(() => initData.value.availableChannels)

  const availableLanguages = initData.value.activeDomain.availableLanguages
    .map((v) => v?.id)
    .filter(notNullish)

  function setChannel(value: Channel) {
    channel.value = value
  }

  function isChannelLandingPage(path: string): boolean {
    return !!availableChannels.value.find((v) => v.landingPage === path)
  }

  return {
    activeDomain: initData.value.activeDomain.enum,
    activeHostname: initData.value.activeDomain.hostname || '',
    availableLanguages,
    availableChannels,
    isProduction,
    activeChannel,
    isChannelLandingPage,
    setChannel,
  }
}

export function setActiveChannel(value?: SetChannelValue) {
  const { setChannel } = useSiteContext()
  setChannel(extractChannel(value))
}
